import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ApiResponse, BeliefBase, BeliefCounts, SettleBeliefResult } from '../../services/api'
import { applyVerdictToCounts } from './beliefCounts'
import { evidenceReceiptTo, typeLabel } from './leastSureFold'
import { refusalFor } from './refusal'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardFooter,
  CardHeader,
  CardList,
  Chip,
  ChipGroup,
  EmptyState,
  ErrorState,
  Eyebrow,
  List,
  LoadingState,
  Meta,
  ReadingSection,
} from '../../ds'

/**
 * What she believes, as fact rows — ONE component for people and products (Knowledge v6
 * PR 2, MAR-326).
 *
 * Extracted from `CustomerKnowledge.tsx`, where the read, the verdict and the counts were
 * written for people alone. The product rail (`PBEL#`, `beliefId`) has the same contract by
 * subject — same body, same three outcomes, same `settledSource` (`docs/reference/api/
 * knowledge.md`) — so a second copy of this file would be two places for one rule to drift.
 * `CustomerKnowledge` and `ProductKnowledge` are thin adapters over it: each supplies a
 * `BeliefSubject` and the copy for its own empty and failed states, and nothing else.
 *
 * ── Evidence is not optional ────────────────────────────────────────────────────────────
 *
 * Every belief renders its `evidence` in full. A believed fact with no provenance is a
 * claim the merchant has no way to judge, and judging it is the entire point of this
 * surface (Receipt test, `docs/brand/product-tests.md`).
 *
 * ── Each row says where it came from ────────────────────────────────────────────────────
 *
 * A source chip (`inferred` / `yours`, see `sourceOf`) and a provenance line — how sure,
 * when formed, and for a settled one how it was settled. ⚠️ The % on a row is THAT belief's
 * own number. Nothing here aggregates them into a figure about the person or the product
 * (spec decision 3).
 *
 * ── Scoping ─────────────────────────────────────────────────────────────────────────────
 *
 * Every cached value names the workspace AND the subject it describes, and is matched at
 * the READ rather than cleared in an effect — React renders once with the new props and the
 * old state before any effect runs, and that render paints one record's beliefs under
 * another's name (`AuthorityPanel.tsx:190-197`, `shell/useNeedsYou.ts:118-128`).
 */

/** Age in the drawer's own idiom. Absent rather than guessed when there is no stamp. */
export function since(iso: string | null | undefined): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return null
  const d = Math.floor((Date.now() - t) / 86_400_000)
  if (d <= 0) return 'today'
  if (d === 1) return 'yesterday'
  if (d < 30) return `${d} days ago`
  // ⚠️ YEARS FIRST. Asking `months < 12` and then dividing by 365 is not the same question
  // twice: 360 days is 12 months by the first measure and 0 years by the second, so a
  // belief just under a year old read "0y ago". The two units have to be decided by one
  // comparison, and the coarser one goes first.
  const y = Math.floor(d / 365)
  if (y >= 1) return `${y}y ago`
  const m = Math.floor(d / 30)
  return `${m} month${m === 1 ? '' : 's'} ago`
}

/**
 * ⚠️ **Three statuses, and they are not two facts flattened into one.**
 *
 * `SETTLED` is about how a belief READS — muted, dashed, carrying a settlement line rather
 * than sitting there awaiting judgment. All three qualify.
 *
 * `reverseVerdict` is about what the merchant may still DO, which is a different question
 * with a different answer for `expired`:
 *
 *  - `confirmed` ⇄ `refuted` are both reversible, and the server means it. `settleHypothesis`
 *    pushes the TTL out on EVERY verdict precisely so a merchant can change their mind, and
 *    `tests/e2e/knowledge-api.test.js` drives `confirmed → refuted` end to end. Offering no
 *    way back made a merchant's mis-click permanent on a path the backend explicitly tests.
 *  - `expired` offers NOTHING, deliberately. Nobody settled it — the synthesiser superseded
 *    its own earlier generation — and `HYPOTHESIS_VERDICTS` is `['refuted','confirmed']`
 *    only, so there is no verdict to reverse and no merchant act to correct. Do not flatten
 *    it back in with the other two.
 */
const SETTLED: ReadonlySet<string> = new Set(['refuted', 'confirmed', 'expired'])
const isSettled = (b: BeliefBase) => !!b.status && SETTLED.has(b.status)

/** The verdict that would undo this one, or `null` when there is nothing to undo. */
export function reverseVerdict(b: BeliefBase): Verdict | null {
  if (b.status === 'confirmed') return 'refuted'
  if (b.status === 'refuted') return 'confirmed'
  return null
}

export type Verdict = 'refuted' | 'confirmed'

/** What each verdict's button says. A reversal is a correction, and reads as one. */
export const VERDICT_LABEL: Record<Verdict, { fresh: string; reverse: string }> = {
  refuted: { fresh: "She's wrong", reverse: "Actually, she's wrong" },
  confirmed: { fresh: "She's right", reverse: "Actually, she's right" },
}

/** The one settled source this surface writes. Read off the row, never assumed. */
export const MERCHANT_SOURCE = 'merchant_knowledge_api'

/**
 * The source chip: where the belief, as it now stands, came from.
 *
 *  - `yours` — settled (`confirmed` or `refuted`) through THIS door. `settledSource` is read
 *    rather than assumed, because a second door would make the assumption false.
 *  - `inferred` — a live theory she formed from evidence (`active`, or a row with no status).
 *  - `null` — neither, and saying either would invent provenance: an `expired` row (the
 *    synthesiser superseded its own earlier generation — nobody's verdict), or a SETTLED row
 *    whose source is anything else. ⚠️ Including an absent `settledSource`: an API-key
 *    verdict omits it, and so do legacy rows. A settled row is not a theory she holds, so
 *    calling it `inferred` would be the one claim it certainly is not.
 */
export function sourceOf(b: BeliefBase): 'inferred' | 'yours' | null {
  if (b.status === 'confirmed' || b.status === 'refuted') return b.settledSource === MERCHANT_SOURCE ? 'yours' : null
  if (b.status === 'active' || b.status == null) return 'inferred'
  return null
}

/** How a settled belief describes its own settling, in the merchant's words. */
export function settlementLine(b: BeliefBase): string {
  const when = since(b.settledAt)
  // `expired` is the synthesiser superseding its own earlier generation — nobody settled
  // it, so saying "you" or naming a source would be inventing provenance.
  if (b.status === 'expired') return `She has let this one lapse${when ? ` · ${when}` : ''}`
  const verb = b.status === 'refuted' ? 'Marked wrong' : 'Confirmed'
  const who = b.settledSource === MERCHANT_SOURCE ? ' here' : ''
  return `${verb}${who}${when ? ` · ${when}` : ''}`
}

// ── The subject adapter ───────────────────────────────────────────────────────────────────

/** One read, already classified. `missing` is a 404 that is not a failure (a product with no card). */
export type BeliefReadResult<D> = { kind: 'read'; data: D } | { kind: 'failed'; refusal?: string } | { kind: 'missing' }

/**
 * What a subject (a person, a product) tells the shared read and verdict about itself.
 *
 * ⚠️ Declare it at MODULE level. Its identity is a hook dependency, so an adapter built
 * inside a render re-reads on every render.
 */
export interface BeliefSubject<B extends BeliefBase, D> {
  /** The id field as the subject stores it — `hypothesisId` or `beliefId`. Never a mix. */
  idOf: (b: B) => string
  read: (workspaceId: string, subjectId: string, opts: { includeSettled: boolean }) => Promise<BeliefReadResult<D>>
  settle: (
    workspaceId: string,
    subjectId: string,
    beliefId: string,
    body: { verdict: Verdict; expectedUpdatedAt: string | null }
  ) => Promise<ApiResponse<SettleBeliefResult<B>>>
  /** The beliefs and counts in a read. `null` beliefs is "could not read", never "none". */
  beliefsOf: (data: D) => { beliefs: B[] | null; counts: BeliefCounts | null }
  /** The same read with its beliefs and counts replaced — everything else untouched. */
  withBeliefs: (data: D, beliefs: B[], counts: BeliefCounts) => D
}

/** Everything derived from one response, stamped with what it describes. */
export interface BeliefSnapshot<D> {
  workspaceId: string
  subjectId: string
  /** Which view this response answers — a narrow read must not be shown as the wide one. */
  includeSettled: boolean
  /** `null` with `failed` or `missing` — never an empty record standing in for either. */
  data: D | null
  failed: boolean
  missing: boolean
  /** The merchant-facing refusal, already mapped from `details.reason`. */
  refusal?: string
}

/** A note about the last verdict attempt, scoped to the record it was fired on. */
interface Feedback {
  workspaceId: string
  subjectId: string
  text: string
  /** A replay and a conflict are not failures; only a real refusal is styled as one. */
  tone: 'ok' | 'bad'
}

const scopeKey = (workspaceId: string, subjectId: string) => `${workspaceId}::${subjectId}`
/** A belief's identity for the in-flight set — the record it belongs to, then the row. */
const busyKey = (workspaceId: string, subjectId: string, beliefId: string) =>
  `${scopeKey(workspaceId, subjectId)}::${beliefId}`

/** What `useBeliefRead` hands an adapter and `BeliefRows`. */
export interface BeliefRead<B extends BeliefBase, D> {
  /** The snapshot for exactly this record AND this view, or `null` while it loads. */
  current: BeliefSnapshot<D> | null
  /**
   * The last snapshot for this record in EITHER view, whatever its outcome — what to say
   * when nothing has ever been read successfully (loading, failed, missing).
   */
  prior: BeliefSnapshot<D> | null
  /**
   * The last SUCCESSFUL snapshot for this record, in either view. ⚠️ For content the toggle
   * does not change (a product's card, summary and gaps; a person's `person`) — never for
   * beliefs, which must come from `current`. A failed WIDER read must cost the merchant the
   * beliefs list alone, not the card or the erasure confirmation's name that a narrower read
   * already had.
   */
  lastRead: BeliefSnapshot<D> | null
  includeSettled: boolean
  toggleSettled: () => void
  note: Feedback | null
  isBusy: (b: B) => boolean
  settle: (b: B, verdict: Verdict) => Promise<void>
  idOf: (b: B) => string
}

/**
 * The read, the verdict and everything they share, for one subject.
 *
 * The rules below were each a bug once, on the customer surface, before this was shared.
 */
export function useBeliefRead<B extends BeliefBase, D>(
  subject: BeliefSubject<B, D>,
  workspaceId: string | undefined,
  subjectId: string | undefined
): BeliefRead<B, D> {
  const [snapshot, setSnapshot] = useState<BeliefSnapshot<D> | null>(null)
  /** The last successful snapshot, kept apart so a failed toggle cannot evict it. */
  const [good, setGood] = useState<BeliefSnapshot<D> | null>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  /** The scope the merchant widened, not a bare boolean — a toggle is per record. */
  const [wideFor, setWideFor] = useState<string | null>(null)
  /**
   * Which beliefs have a verdict in flight, keyed by workspace + subject + belief.
   *
   * ⚠️ A single slot was three bugs, not a simplification. Click A then B and the slot
   * pointed at B, so A's buttons re-enabled WHILE A's POST was still in flight — a second
   * click then sent the `expectedUpdatedAt` A was rendered with, which the first write had
   * already superseded, and the merchant was told "she changed her mind while you were
   * looking" about their own click. And whichever request finished first cleared the slot,
   * so B stopped looking busy early. A set closes all three.
   *
   * Scoped like every other cached value here: two records can hold the same belief id,
   * since it is derived from evidence rather than from the person or product.
   */
  const [settling, setSettling] = useState<ReadonlySet<string>>(() => new Set())

  const key = workspaceId && subjectId ? scopeKey(workspaceId, subjectId) : null

  /**
   * The record on screen RIGHT NOW, read at CALL time. A value captured in a closure is
   * exactly what goes stale when the merchant closes one drawer and opens another while a
   * request is in flight.
   *
   * ⚠️ Written in an effect, never during render. A ref mutated in the render body is a
   * write React may throw away — the render can be abandoned — and `react-hooks` rejects
   * it outright. It is declared ABOVE the load effect on purpose: passive effects run in
   * declaration order, so this is already current by the time a load for that render starts.
   */
  const liveKey = useRef<string | null>(null)
  useEffect(() => {
    liveKey.current = key
  })

  /** Is `asked` still the record on screen? Reads the ref, never a captured value. */
  const stillCurrent = useCallback((asked: string) => liveKey.current === asked, [])

  /** Request generation per record: only the newest response for a record may write. */
  const requestSeq = useRef<Record<string, number>>({})

  // ── Scoped at the read. A value describing another record is simply not current. ──
  const includeSettled = key != null && wideFor === key
  const prior =
    snapshot && snapshot.workspaceId === workspaceId && snapshot.subjectId === subjectId ? snapshot : null
  const current = prior && prior.includeSettled === includeSettled ? prior : null
  const lastRead = good && good.workspaceId === workspaceId && good.subjectId === subjectId ? good : null
  const note =
    feedback && feedback.workspaceId === workspaceId && feedback.subjectId === subjectId ? feedback : null

  const load = useCallback(
    async(ws: string, id: string, wide: boolean) => {
      const k = scopeKey(ws, id)
      const seq = (requestSeq.current[k] = (requestSeq.current[k] ?? 0) + 1)
      const apply = (snap: BeliefSnapshot<D>) => {
        if (requestSeq.current[k] !== seq) return
        // A response for a record the merchant has left must not touch the shared slot at
        // all — not even correctly-scoped data, because it evicts what the current one put
        // there and leaves that record stuck on its loading line with no request to rescue it.
        if (!stillCurrent(k)) return
        setSnapshot(snap)
        if (snap.data) setGood(snap)
      }
      const base = { workspaceId: ws, subjectId: id, includeSettled: wide }
      try {
        const res = await subject.read(ws, id, { includeSettled: wide })
        if (res.kind === 'read') apply({ ...base, data: res.data, failed: false, missing: false })
        else if (res.kind === 'missing') apply({ ...base, data: null, failed: false, missing: true })
        else apply({ ...base, data: null, failed: true, missing: false, refusal: res.refusal })
      } catch {
        // ⚠️ Unreachable through the production client, and kept deliberately.
        // `ApiClient.request` wraps its whole body in a try/catch and always RESOLVES, so
        // nothing awaited here rejects today — which also means each adapter's default
        // `failed` copy never renders in production. It is here for a caller that hands in
        // a different client, and for the day that wrapper stops being total; it is not a
        // claim that a rejection is expected.
        apply({ ...base, data: null, failed: true, missing: false })
      }
    },
    [subject, stillCurrent]
  )

  useEffect(() => {
    if (!workspaceId || !subjectId) return
    void load(workspaceId, subjectId, includeSettled)
  }, [load, workspaceId, subjectId, includeSettled])

  /**
   * Put one belief back where it was, by id.
   *
   * ⚠️ **In place.** Never re-sorted, never filtered out. A belief the merchant has just
   * settled stays exactly where they settled it and changes how it reads — removing it
   * would make their own action look like the record vanishing, and re-ordering would move
   * the thing they are looking at out from under them.
   */
  const replaceBelief = useCallback(
    (ws: string, id: string, next: B) => {
      setSnapshot((prev) => {
        if (!prev || prev.workspaceId !== ws || prev.subjectId !== id || !prev.data) return prev
        const { beliefs, counts } = subject.beliefsOf(prev.data)
        if (!beliefs || !counts) return prev
        const nextId = subject.idOf(next)
        // The row replacement is id-matched, in place and idempotent, and is NEVER gated —
        // the 409 re-render contract depends on it landing whatever the counts do.
        const replaced = beliefs.map((b) => (subject.idOf(b) === nextId ? next : b))
        return {
          ...prev,
          data: subject.withBeliefs(prev.data, replaced, applyVerdictToCounts(counts, beliefs, next, subject.idOf)),
        }
      })
    },
    [subject]
  )

  const settle = useCallback(
    async(belief: B, verdict: Verdict) => {
      if (!workspaceId || !subjectId) return
      const ws = workspaceId
      const id = subjectId
      const beliefId = subject.idOf(belief)
      const token = busyKey(ws, id, beliefId)
      setSettling((prev) => new Set(prev).add(token))
      setFeedback(null)
      try {
        const res = await subject.settle(ws, id, beliefId, {
          verdict,
          // ⚠️ **Exactly as the read returned it.** The read collapsed the rows for this
          // id through the shared `selectHypothesisRow` — settled-over-active, with no
          // expectation — and handed back THAT row's `updatedAt`; the write re-picks on this
          // exact value through the same function. They agree because they are one
          // function, not because they were written to match. Duplicate rows exist because,
          // until MAR-323 fixed MAR-182, a confirmed row was neither tombstoned nor expired,
          // so the next synthesis run wrote a fresh `active` twin of it; those rows live
          // until their 90-day TTL. Re-deriving this stamp, or sorting the list between the
          // read and this call, aims the verdict at a row the merchant never saw — and for a
          // confirm-then-refute it 409s forever, because the expectation can then never match.
          expectedUpdatedAt: belief.updatedAt,
        })

        if (res.success && res.data) {
          if (res.data.belief) replaceBelief(ws, id, res.data.belief)
          // ⚠️ `alreadySettled` is a SUCCESS. It means the row is already in the state the
          // merchant asked for — the second press of a button that worked. Showing a failure
          // here is how a surface teaches people to distrust it.
          setFeedback({
            workspaceId: ws,
            subjectId: id,
            tone: 'ok',
            text: res.data.alreadySettled
              ? 'Already recorded — she had this one settled.'
              : verdict === 'refuted'
                ? 'Recorded. She will not put this theory back from the same evidence.'
                : 'Recorded.',
          })
          return
        }

        const reason = (res.details as { reason?: unknown } | undefined)?.reason
        // ⚠️ A 409 carries the belief AS IT NOW STANDS. Re-rendering it in place is the whole
        // point of the status — the next attempt then has a token that can match. Telling the
        // merchant to reload and go find it again is exactly what `details.current` exists to
        // avoid. The token is the same on both subjects (`verdict.js` is one mapping).
        const fresh = (res.details as { current?: B } | undefined)?.current
        if (reason === 'hypothesis_write_conflict' && fresh) {
          replaceBelief(ws, id, fresh)
        }
        setFeedback({ workspaceId: ws, subjectId: id, tone: 'bad', text: refusalFor(res, 'Nothing was recorded.') })
      } finally {
        // Only THIS belief's flag. Clearing the whole slot is what let one request's
        // completion tell the merchant a different one had finished.
        setSettling((prev) => {
          const next_ = new Set(prev)
          next_.delete(token)
          return next_
        })
      }
    },
    [workspaceId, subjectId, subject, replaceBelief]
  )

  const isBusy = useCallback(
    (b: B) => !!workspaceId && !!subjectId && settling.has(busyKey(workspaceId, subjectId, subject.idOf(b))),
    [workspaceId, subjectId, settling, subject]
  )

  const toggleSettled = useCallback(() => setWideFor(includeSettled ? null : key), [includeSettled, key])

  return { current, prior, lastRead, includeSettled, toggleSettled, note, isBusy, settle, idOf: subject.idOf }
}

// ── The rows ──────────────────────────────────────────────────────────────────────────────

/** What the section is showing. Loading, could-not-read and none are never one render. */
export type BeliefRowsState<B> =
  | { kind: 'loading' }
  | { kind: 'failed'; text: string }
  | { kind: 'ready'; beliefs: B[]; counts: BeliefCounts | null }

/**
 * The "What she believes" section: three read states, then one fact row per belief, the
 * verdict note and the settled toggle.
 */
export default function BeliefRows<B extends BeliefBase, D>({
  read,
  state,
  loadingText,
  noneText,
}: {
  read: BeliefRead<B, D>
  state: BeliefRowsState<B>
  loadingText: string
  noneText: string
}) {
  return (
    <ReadingSection label="What she believes" testId="kn-beliefs">
      {state.kind === 'loading' ? (
        // 1. Loading. Never conflated with either of the two below.
        <LoadingState caption={loadingText} />
      ) : state.kind === 'failed' ? (
        // 2. Could not read. ⚠️ NOT an empty list — see each adapter for why the server
        //    answers so that this can be said out loud.
        <ErrorState title={state.text} />
      ) : state.beliefs.length > 0 ? (
        <CardList>
          {/* ⚠️ In the order the server gave them. See the note on `expectedUpdatedAt`. */}
          {state.beliefs.map((b) => (
            <BeliefBlock key={read.idOf(b)} belief={b} busy={read.isBusy(b)} onSettle={read.settle} />
          ))}
        </CardList>
      ) : (
        // 3. Genuinely nothing believed — a read that worked and found no theory.
        <EmptyState size="sm">{noneText}</EmptyState>
      )}

      {read.note && (
        <Alert tone={read.note.tone === 'bad' ? 'danger' : 'success'} data-testid="kn-note" data-tone={read.note.tone}>
          {read.note.text}
        </Alert>
      )}

      {state.kind === 'ready' && state.counts && (
        <SettledToggle counts={state.counts} includeSettled={read.includeSettled} onToggle={read.toggleSettled} />
      )}
    </ReadingSection>
  )
}

/**
 * One fact row: the type key, the sentence, the evidence, where it came from, how sure she
 * is and how old it is, and the verdicts.
 *
 * A settled belief must read as VISIBLY settled rather than as a live theory with a
 * disabled button beside it: the merchant already told her, and showing it as something
 * still awaiting their judgment is the surface forgetting what they said. That is about
 * how it READS — it still offers the one act that is left, which is taking the verdict
 * back. See the note on `SETTLED`.
 */
export function BeliefBlock<B extends BeliefBase>({
  belief,
  busy,
  onSettle,
}: {
  belief: B
  busy: boolean
  onSettle: (b: B, verdict: Verdict) => void
}) {
  const settled = isSettled(belief)
  const reverse = reverseVerdict(belief)
  const source = sourceOf(belief)
  // ⚠️ Two different facts, and `||` collapsed them. `generatedAt` is when she formed the
  // theory; `updatedAt` is when the row last moved — a settle, or the synthesiser touching
  // it. Only the second is the concurrency token, and labelling either as "formed" would
  // make the drawer say something the row does not.
  const formed = since(belief.generatedAt)
  const changed = belief.updatedAt && belief.updatedAt !== belief.generatedAt ? since(belief.updatedAt) : null
  // ⚠️ A settled or expired row's number is HISTORY — how sure she was before it stopped
  // being a live theory. Printed bare, it reads as a current belief she does not hold.
  const live = belief.status === 'active' || belief.status == null
  const pct = belief.confidence != null ? `${Math.round(belief.confidence * 100)}% sure` : null
  const confidence = pct && !live ? `was ${pct}` : pct

  return (
    <Card
      padding="sm"
      tone={settled ? 'recessed' : 'plain'}
      data-testid="kn-bel"
      data-settled={settled ? 'true' : 'false'}
      data-status={belief.status || 'unknown'}
    >
      <CardHeader
        titleAs="p"
        title={belief.hypothesis || 'This theory has lost its wording.'}
        aside={
          belief.type ? (
            // The type in the merchant's words ("Fit"), never the stored key (MAR-412).
            <Badge data-testid="kn-key" data-type={belief.type}>
              {typeLabel(belief.type)}
            </Badge>
          ) : undefined
        }
      />

      {/* ⚠️ ALWAYS rendered, and never summarised. A believed fact with no provenance is a
          claim the merchant has no way to judge, and judging it is the point of this
          surface. An empty list is itself worth saying out loud. */}
      <div data-testid="kn-ev">
        <Eyebrow tone="muted">Because</Eyebrow>
        {belief.evidence.length > 0 ? (
          <List>
            {belief.evidence.map((e, i) => {
              // MAR-390: linked only when the server attached a receipt to a ref on THIS item.
              const to = evidenceReceiptTo(belief, i)
              return (
                <li key={i}>
                  {to ? (
                    <Link to={to} data-testid="kn-ev-link">
                      {e}
                      {/* The visible text is the evidence; say where the link goes. */}
                      <span className="mv-sr"> — open receipt</span>
                    </Link>
                  ) : (
                    e
                  )}
                </li>
              )
            })}
          </List>
        ) : (
          <p>She recorded no evidence for this one, which is why it carries no weight.</p>
        )}
      </div>

      <ChipGroup>
        {source && (
          <Chip
            tone={source === 'yours' ? 'sage' : 'neutral'}
            data-source={source}
            title={source === 'yours' ? 'You settled this' : 'She inferred this from evidence'}
          >
            {source}
          </Chip>
        )}
        <Meta data-testid="kn-meta">
          {[confidence, formed && `formed ${formed}`, changed && `changed ${changed}`].filter(Boolean).join(' · ') ||
            '—'}
        </Meta>
      </ChipGroup>

      {settled ? (
        <>
          <p data-testid="kn-settled">{settlementLine(belief)}</p>
          {/* ⚠️ A mis-click must not be permanent. `reverse` is null for `expired` — see
              the note on SETTLED — so that one keeps its settlement line and nothing else. */}
          {reverse && (
            <CardFooter>
              <Button
                variant={reverse === 'refuted' ? 'ghost' : 'sage'}
                size="sm"
                disabled={busy}
                onClick={() => onSettle(belief, reverse)}
              >
                {busy ? 'Recording…' : VERDICT_LABEL[reverse].reverse}
              </Button>
            </CardFooter>
          )}
        </>
      ) : (
        <CardFooter>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onSettle(belief, 'refuted')}>
            {busy ? 'Recording…' : VERDICT_LABEL.refuted.fresh}
          </Button>
          <Button variant="sage" size="sm" disabled={busy} onClick={() => onSettle(belief, 'confirmed')}>
            {busy ? 'Recording…' : VERDICT_LABEL.confirmed.fresh}
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}

/**
 * The "and N settled" affordance.
 *
 * ⚠️ Built from `counts`, which breaks down ALL FOUR statuses whether or not they are being
 * shown. So the offer can be made — or withheld — without a speculative second read, and it
 * says a real number rather than "see more".
 */
export function SettledToggle({
  counts,
  includeSettled,
  onToggle,
}: {
  counts: BeliefCounts
  includeSettled: boolean
  onToggle: () => void
}) {
  const settledCount = counts.refuted + counts.expired
  if (settledCount === 0) return null
  return (
    <Button variant="link" onClick={onToggle}>
      {includeSettled
        ? 'Hide what she has settled'
        : `…and ${settledCount} she has settled${counts.refuted > 0 ? ` (${counts.refuted} you marked wrong)` : ''}`}
    </Button>
  )
}
